/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { BoxView, Button, View, Text, Avatar } from '@components/atoms'
import { Grid } from '@grid'
import { customerColumns } from '@grid/columns/main/CustomerColumn'
import {
	useAppSelector,
	useAppDispatch,
	customerActions,
	ApiGetDataStatus,
} from '@redux/index'
import { CustomerType } from '@schemas'
import {
	GridRenderCellParams,
	GridRenderEditCellParams,
	GridRowModel,
} from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import {
	Checkbox,
	ListItemText,
	MenuItem,
	Select,
	SelectChangeEvent,
} from '@mui/material'
import {
	CUSTOMER_ROLE_OPTIONS,
	isCustomerRoleSelectable,
	sortCustomerRoles,
} from '@src/configs/customerRole.config'

/**
 * 고객 유형 다중 선택. 셀 편집 상태에서 그려진다.
 * Dealer 를 고르면 Distributor 가 잠기고, 그 반대도 같다(customerRole.config 의 배타 쌍).
 * 새 행(isNew)은 아직 고객 ID 가 없어 역할을 붙일 수 없으므로 비활성.
 */
const CustomerRolesEditCell = (params: GridRenderEditCellParams) => {
	const selected: string[] = Array.isArray(params.value) ? params.value : []

	const onChange = (event: SelectChangeEvent<string[]>) => {
		const raw = event.target.value
		const next = sortCustomerRoles(typeof raw === 'string' ? raw.split(',') : raw)
		void params.api.setEditCellValue({ id: params.id, field: params.field, value: next })
	}

	return (
		<Select
			multiple
			autoFocus
			fullWidth
			size="small"
			variant="standard"
			disabled={params.row.isNew === true}
			value={selected}
			onChange={onChange}
			renderValue={value =>
				sortCustomerRoles(value)
					.map(
						aCode =>
							CUSTOMER_ROLE_OPTIONS.find(aOption => aOption.value === aCode)?.label ??
							aCode,
					)
					.join(' + ')
			}
			sx={{ height: '100%', px: 1 }}
		>
			{CUSTOMER_ROLE_OPTIONS.map(aOption => {
				const isSelectable = isCustomerRoleSelectable(aOption.value, selected)
				return (
					<MenuItem key={aOption.value} value={aOption.value} disabled={!isSelectable}>
						<Checkbox size="small" checked={selected.includes(aOption.value)} />
						<ListItemText
							primary={aOption.label}
							secondary={isSelectable ? undefined : 'Dealer / Distributor 중 하나만'}
						/>
					</MenuItem>
				)
			})}
		</Select>
	)
}

const CustomerPage = () => {
	const reduxDispatch = useAppDispatch()
	const navigate = useNavigate()

	// redux state
	const customerList: CustomerType[] = useAppSelector(
		state => state.customer.customerList,
	)
	const status: ApiGetDataStatus = useAppSelector(
		state => state.customer.customerListStatus,
	)

	const onAction = (iudFlag: string, aRow: GridRowModel) => {
		// 고객 유형은 고객 수정 API 와 다른 곳(PUT /customer-role/{id})에 저장된다.
		// 바뀐 행에서만 따로 보낸다 — 새 행은 ID 가 없고, 삭제는 역할까지 같이 없어진다.
		if (iudFlag === 'U') {
			const before = sortCustomerRoles(
				customerList.find(aCustomer => aCustomer.id === aRow.id)?.roles ?? [],
			)
			const after = sortCustomerRoles(Array.isArray(aRow.roles) ? aRow.roles : [])
			if (before.join(',') !== after.join(',')) {
				reduxDispatch(
					customerActions.UPDATE_CUSTOMER_ROLES({
						customerId: aRow.id,
						roleCodes: after,
					}),
				)
			}
		}

		const newData = {
			iudFlag: iudFlag as 'I' | 'U' | 'D',
			customerId: aRow.id,
			name: aRow.name,
			email: aRow.email,
			profileColor: aRow.profileColor,
			verifiedYn: aRow.verifiedYn,
			createdAt: aRow.createdAt,
			confirmEmailNewsYn: aRow.confirmEmailNewsYn,
			password: { password: aRow.password || '' },
		}
		reduxDispatch(customerActions.UPDATE_CUSTOMER({ ...newData }))
	}

	return (
		<View css={style}>
			<BoxView title="고객 관리">
				<Grid
					title="고객 관리"
					rows={customerList}
					columns={customerColumns}
					loading={status === 'loading'}
					onAction={onAction}
					renderCell={{
						profileColor: (
							params: GridRenderCellParams<
								{ id: string; name: string; color: string },
								any,
								any
							>,
						) => {
							if (params.value === null) {
								return <></>
							}

							return (
								<Avatar
									picture={''}
									name={params.value.name}
									color={params.value.color}
								/>
							)
						},
						password: (params: GridRenderCellParams) => {
							return <Text>********</Text>
						},
					}}
					/* /admin/users 와 같은 이유 — 버튼은 수정 모드를 켜는 순간 보여야 한다.
					   renderEditCell 은 셀을 더블클릭해야 그려진다. */
					renderEditCell={{
						roles: (params: GridRenderCellParams) => (
							<CustomerRolesEditCell
								{...(params as unknown as GridRenderEditCellParams)}
							/>
						),
					}}
					renderEditModeCell={{
						password: (params: GridRenderCellParams) => {
							const onClick = () => {
								navigate('/main/reset-customer-password')
								reduxDispatch(customerActions.EDIT_PASSWORD_CUSTOMER({ ...params.value }))
							}

							if (params.row.isNew) {
								return <></>
							}

							return (
								<Button variant="outlined" size="small" onClick={onClick}>
									수정
								</Button>
							)
						},
					}}
				/>
			</BoxView>
		</View>
	)
}

export default CustomerPage

// 높이 체인 — 그리드가 남은 화면을 다 받고 페이지는 스크롤하지 않는다(오더 관리와 같게, 2026-09-27)
const style = css({ height: '100%' })
